import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import Input from "components/DesignSystem/Input/Input";
import Modal from "components/DesignSystem/Modal/Modal";
import { SwitchField } from "components/DesignSystem/Switch/SwitchField";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useToast } from "hooks/useToast";
import {
  CouponTaskCategoryMapping,
  DiscountType,
  TaskTemplateCategory,
  useCreateCouponTaskCategoryMappingMutation,
  useUpdateCouponTaskCategoryMappingMutation,
} from "store/apis/couponAdmin";
import * as yup from "yup";
import {
  DISCOUNT_TYPE_OPTIONS,
  getErrorMessage,
  getOneYearFromTodayInputValue,
  toDateInputValue,
  toEndOfDayIso,
} from "./couponUtils";

type FormValues = {
  task_template_category_id: string;
  discount_type: DiscountType;
  discount_rate: number | "";
  expiry: string;
  allowed_redemption: number | "";
  is_free_task_coupon: boolean;
};

const validationSchema = yup.object({
  task_template_category_id: yup.string().required("Category is required"),
  discount_type: yup.string().required("Discount type is required"),
  discount_rate: yup
    .number()
    .typeError("Discount is required")
    .required("Discount is required")
    .min(0, "Discount can't be negative")
    .when("discount_type", {
      is: "PERCENT",
      then: (schema) => schema.max(100, "Percent can't exceed 100"),
    }),
  // Billing skips mappings with no expiry, so it is mandatory here
  expiry: yup.string().required("Expiry is required"),
  allowed_redemption: yup
    .number()
    .transform((value, original) => (original === "" ? undefined : value))
    .min(0, "Can't be negative"),
});

type TaskCategoryMappingModalProps = {
  isOpen: boolean;
  close: () => void;
  couponId: string;
  categories: TaskTemplateCategory[];
  // Edit mode when a mapping is passed
  mapping?: CouponTaskCategoryMapping;
};

export const TaskCategoryMappingModal = ({
  isOpen,
  close,
  couponId,
  categories,
  mapping,
}: TaskCategoryMappingModalProps) => {
  const { alertToast, successToast } = useToast();
  const [createMapping, { isLoading: isCreating }] =
    useCreateCouponTaskCategoryMappingMutation();
  const [updateMapping, { isLoading: isUpdating }] =
    useUpdateCouponTaskCategoryMappingMutation();
  const isEdit = Boolean(mapping);

  const categoryOptions = categories.map((category) => ({
    label: category.title,
    value: category.uuid,
  }));

  const initialValues: FormValues = {
    task_template_category_id: mapping?.task_template_category.uuid ?? "",
    discount_type: mapping?.discount_type ?? "PERCENT",
    discount_rate: mapping?.discount_rate ?? "",
    expiry: mapping
      ? toDateInputValue(mapping.expiry)
      : getOneYearFromTodayInputValue(),
    allowed_redemption: mapping?.allowed_redemption ?? "",
    is_free_task_coupon: mapping?.is_free_task_coupon ?? false,
  };

  const onSubmit = async (values: FormValues) => {
    const payload = {
      discount_type: values.discount_type,
      discount_rate: Number(values.discount_rate),
      expiry: toEndOfDayIso(values.expiry),
      allowed_redemption:
        values.allowed_redemption === ""
          ? null
          : Number(values.allowed_redemption),
      is_free_task_coupon: values.is_free_task_coupon,
    };
    try {
      if (mapping) {
        await updateMapping({
          couponId,
          mappingId: mapping.uuid,
          payload,
        }).unwrap();
      } else {
        await createMapping({
          couponId,
          payload: {
            ...payload,
            task_template_category_id: values.task_template_category_id,
          },
        }).unwrap();
      }
      successToast({ message: isEdit ? "Mapping updated" : "Mapping added" });
      close();
    } catch (err) {
      alertToast({ message: getErrorMessage(err) });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content asChild>
        <FormikForm<FormValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({ values }) => (
            <>
              <Modal.Header>
                <Modal.Title>
                  {isEdit ? "Edit task discount" : "Add task discount"}
                </Modal.Title>
                <Modal.Close />
              </Modal.Header>

              <Modal.Body className="t-flex t-flex-col t-gap-4">
                <Combobox
                  withForm
                  name="task_template_category_id"
                  label="Task template category"
                  placeholder="e.g. DFT, F1099, FBAR, 7004"
                  required
                  isDisabled={isEdit}
                  isClearable={false}
                  menuPortalTarget={document.body}
                  options={categoryOptions}
                  value={
                    categoryOptions.find(
                      (option) =>
                        option.value === values.task_template_category_id
                    ) ?? null
                  }
                />
                <div className="t-grid t-grid-cols-2 t-gap-4">
                  <Combobox
                    withForm
                    name="discount_type"
                    label="Discount type"
                    required
                    isClearable={false}
                    menuPortalTarget={document.body}
                    options={DISCOUNT_TYPE_OPTIONS}
                    value={
                      DISCOUNT_TYPE_OPTIONS.find(
                        (option) => option.value === values.discount_type
                      ) ?? null
                    }
                  />
                  <Input
                    withForm
                    name="discount_rate"
                    type="number"
                    min={0}
                    label={
                      values.discount_type === "PERCENT"
                        ? "Discount (%)"
                        : "Discount ($)"
                    }
                    required
                  />
                </div>
                <div className="t-grid t-grid-cols-2 t-gap-4">
                  <DateField name="expiry" label="Expiry" required />
                  <Input
                    withForm
                    name="allowed_redemption"
                    type="number"
                    min={0}
                    label="Allowed redemptions"
                    placeholder="Free tasks only"
                  />
                </div>
                <SwitchField
                  name="is_free_task_coupon"
                  label="This is the coupon's free task"
                  description="Only one category per coupon can be the free task. Use 100% off."
                />
              </Modal.Body>

              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="button">Cancel</Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  type="submit"
                  isLoading={isCreating || isUpdating}
                  disabled={isCreating || isUpdating}
                >
                  {isEdit ? "Save" : "Add"}
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
